var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/*
 * <vitesse-search> — the product's search bar, as an element.
 *
 * `TreeSearch` is a framework-agnostic controller that attaches to a specific piece of DOM: an
 * input, a token-highlight overlay, a clear button and a ⌘/ hint, in that order and with those
 * class names. Every host that wanted the shared search therefore had to hand-write that markup
 * correctly, and three of them do — which means three copies of a structure whose contract is
 * "get this exactly right or the overlay silently misaligns".
 *
 * This is that markup, once, with the controller already attached. A host says what it is searching
 * and gets the same bar the symbol tree has.
 *
 * IT AUTHORS NO CSS. The rules are `styles/components/base-search.scss`, which moved out of the
 * symbol grid alongside the controller. It renders into the LIGHT DOM so those global rules reach
 * it — a shadow root here would seal the search off from its own stylesheet.
 */
import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { TreeSearch } from '../search';
import { t } from '../i18n';
import { LocaleController } from '../i18n/LocaleController';
let VitesseSearch = class VitesseSearch extends LitElement {
    constructor() {
        super(...arguments);
        /** The `/command:value` vocabulary. Empty is fine and common — free-text search still works, the
         *  autocomplete simply has nothing to offer. */
        this.commands = [];
        this.placeholder = '';
        /** Mirrors the field only so the clear button can appear and disappear. The INPUT ITSELF is not
         *  Lit-bound: the controller owns the field, its selection and its overlay, and re-rendering a
         *  value into it mid-type would fight the caret. */
        this.query = '';
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- re-renders on locale change
        this.locale = new LocaleController(this);
    }
    createRenderRoot() {
        return this;
    }
    firstUpdated() {
        const wrap = this.querySelector('.vg-search-wrap');
        if (!wrap)
            return;
        this.search = new TreeSearch(wrap, {
            commands: this.commands,
            ctx: () => (this.ctx?.() ?? undefined),
            iterate: () => this.iterate?.() ?? null,
            valueForCommand: (cmd, item) => this.valueForCommand?.(cmd, item),
            textMatch: (item, q, ctx) => this.textMatch?.(item, q, ctx) ?? true,
            onChange: (q) => {
                this.query = q;
                this.onQuery?.(q);
            },
        });
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        // The controller holds document-level key handlers for ⌘/ — leaving them behind means a
        // shortcut that focuses an input which is no longer on the page.
        this.search?.destroy();
        this.search = undefined;
    }
    render() {
        // STRUCTURE IS THE CONTRACT. The controller finds these by class name and positions the overlay
        // against the input's own box, so the order and the names are not styling choices.
        const mac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '');
        return html `<div class="vg-search-wrap">
      <vitesse-icon class="vg-search-icon" name="search" size="14"></vitesse-icon>
      <div class="vg-search-highlight"></div>
      <input class="vg-search-input" type="text" placeholder=${this.placeholder || t('ai.searchConversations')} spellcheck="false" autocomplete="off" />
      ${this.query
            ? html `<button class="vg-search-clear" title=${t('ai.clearSearch')} @click=${() => this.clear()}>
            <vitesse-icon name="x" size="13"></vitesse-icon>
          </button>`
            : nothing}
      <kbd class="vg-search-shortcut"><span>${mac ? '⌘' : 'Ctrl'}</span>/</kbd>
    </div>`;
    }
    /** Empty the field, through the controller — setting `.value` directly would leave the overlay
     *  showing tokens for text that is no longer there. */
    clear() {
        this.search?.setValue('');
        this.query = '';
        this.onQuery?.('');
    }
};
__decorate([
    property({ attribute: false })
], VitesseSearch.prototype, "commands", void 0);
__decorate([
    property({ attribute: false })
], VitesseSearch.prototype, "iterate", void 0);
__decorate([
    property({ attribute: false })
], VitesseSearch.prototype, "ctx", void 0);
__decorate([
    property({ attribute: false })
], VitesseSearch.prototype, "valueForCommand", void 0);
__decorate([
    property({ attribute: false })
], VitesseSearch.prototype, "textMatch", void 0);
__decorate([
    property({ attribute: false })
], VitesseSearch.prototype, "onQuery", void 0);
__decorate([
    property()
], VitesseSearch.prototype, "placeholder", void 0);
__decorate([
    state()
], VitesseSearch.prototype, "query", void 0);
VitesseSearch = __decorate([
    customElement('vitesse-search')
], VitesseSearch);
export { VitesseSearch };
//# sourceMappingURL=vitesse-search.js.map